// FLEET · MISSIONS (plan §3.1, R84, R1888; ui-map-interface.md §12
// "FLEET · MISSIONS"): the command deck. Fleets at sea on top; the hulls
// in the pens at the bottom, each with the step that blocks its deploy as
// its press and the rest in ⋯. The pens live here and in no other menu
// (R1888 superseded the split of R84, which prepared a hull in DOCK ·
// PENS and dispatched it from here).
//
// The page is a pure function of the state and of the readers the overlay
// hands it; the rows are the fleet rows of `ui/fleetlist.ts` (R1816).

import { JobKind } from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { dur } from '../../state';
import type { SceneLook } from '../../render/scene-look';
import type { CardEnv } from '../cards/card';
import type { MapCardCtx } from '../cards/mapcards';
import { esc } from '../esc';
import { dockedRow, fleetRowHtml, noteRowHtml, seaRow } from '../fleetlist';
import { emptyGoHtml } from './howfold';
import { diveName, sitePlace, windowText } from './markup';

/** What FLEET · MISSIONS reads beside the state. */
export interface MissionsCtx {
  /** The map cards' reader: the state and the own fleets at sea. */
  card: MapCardCtx;
  env: CardEnv;
  /** The reader's look, whose ship icon set draws the hull marks (R1844). */
  look?: SceneLook;
  /** The fleet row whose ⋯ stands open, or 0 (R1816). */
  rowMore: number;
  /** The penned hull whose GO… pick waits, or 0. */
  deploying: number;
  /** The picker card that stands for a hull in the pens: the captain
   *  picker or the LOADOUT sheet (R1893, R2031), or null. */
  dialog: { kind: 'cap' | 'load' | 'stow'; fleetId: number } | null;
  /** The overlay's arm gate (R696): BREAK draws its armed face. */
  armed: (key: string) => boolean;
}

/** The page of FLEET · MISSIONS. */
export function missionsHtml(ctx: MissionsCtx): string {
  const s = ctx.card.s;
  const e = s.empire;
  const list = ctx.card.fleets();
  // The numerator is the server's own count, not the length of this list
  // (R680). A running dive holds a mission slot as surely as a hull at sea
  // does (numbers.md §11), so a header that counted hulls alone read 8/10
  // while the deploy refused with "mission limit 10 reached". The dives
  // are listed below the hulls for the same reason: the player must see
  // what holds every slot the header counts.
  const dives = e?.expeditions ?? [];
  // A dive that still waits in the build queue holds a slot too (R738):
  // the server counts the prep job from the moment the player commits the
  // captain. So the list names it, for the same reason it names the
  // running dives.
  const outfitting = s.queue.filter((j) => j.kind === JobKind.JOB_KIND_EXPEDITION);
  // A pledged fleet holds a slot for the same reason (numbers.md §8: "a
  // pledge frees nothing at home"), and it is off the map (R76), so no
  // State frame carries it and the sea list above cannot show it. The
  // Sieges frame names every pledge this player holds, because the server
  // permits a pledge at a control point only.
  const pledged = s.sieges.filter((g) => g.youCommitted)
    .flatMap((g) => g.yourFleets.map((f) => ({ name: f.name, site: g })));
  let html = `<h3>${S.fleets_head({
    n: e?.missions
      ?? (list.length + dives.length + outfitting.length + pledged.length),
    cap: e?.missionCap ?? 0,
  })}</h3>`;
  if (list.length === 0 && dives.length === 0
    && outfitting.length === 0 && pledged.length === 0) {
    html += `<div class="empty">${S.fleets_empty}</div>`;
  }
  // The fleet rows (R1816): one row of two lines for every hull, at sea
  // or in the pens, combat or support, from `ui/fleetlist.ts`. The hull
  // mark of a row wears the ship icon set of the reader's look (R1844).
  const { env, look } = ctx;
  // A dive's remaining figure is NOT queue work, so it is never divided
  // by the queue rate (R688): `expeditions.go` drains it with a bare
  // `Remaining -= dt`. Only the prep of a dive is a job, and no menu
  // states that.
  for (const x of dives) {
    html += noteRowHtml(x.captainName, S.fleets_dive,
      S.expedition_running({ depth: x.depth, left: dur(x.remainingSec) }));
  }

  // One row per dive still in the build queue (R738). The row carries no
  // clock: the prep is queue work, and the queue strip and BUILD · QUEUE
  // already state the one honest figure for it (R688). Line 2 is the
  // sentence the wardroom card says about the same captain.
  for (const j of outfitting) {
    html += noteRowHtml(s.captain(j.target)?.name ?? '?', S.fleets_dive,
      S.captain_outfitting({ dive: esc(diveName(s, j.count)) }));
  }

  // The hulls at sea stand under a Combat and a Support head, as the pens
  // below do, and each head counts its own rows; the head above keeps the
  // total. A pledged fleet counts as combat, because a support hull never
  // pledges. A dive is no hull, so the dives stand right under the total.
  const sel = s.selection;
  const supportCls = (cls: number) => s.ship(cls)?.support ?? false;
  for (const support of [false, true]) {
    const rows = list.filter((f) => supportCls(f.cls) === support);
    const extra = support ? 0 : pledged.length;
    if (rows.length + extra === 0) continue;
    html += `<h3>${support
      ? S.fleets_head_support({ n: rows.length })
      : S.fleets_head_combat({ n: rows.length + extra })}</h3>`;
    for (const f of rows) {
      html += fleetRowHtml(seaRow(ctx.card, f, {
        look, moreOpen: ctx.rowMore === f.id,
        selected: sel?.kind === 'fleet' && sel.id === f.id,
      }), env);
    }
    if (!support) {
      // One row per pledged fleet (R733). The row gives the object the fleet
      // stands at and the time to the window, which is when the pledge
      // resolves and the hull comes home. SHOW is the only control: a pledged
      // fleet takes no order until the window closes. SHOW opens the card
      // of the pledged object (R2517).
      for (const p of pledged) {
        html += noteRowHtml(p.name, S.fleets_pledged, S.fleets_pledged_at({
          place: esc(sitePlace(p.site)), window: windowText(s, p.site),
        }), `<button data-act="focussite" data-id="${p.site.siteId}" data-n="${p.site.cell}">${S.btn_show}</button>`);
      }
    }
  }

  // The pens (R1888, which supersedes the split of R84): every hull in
  // the pens stands here and nowhere else. Line 2 reads the prep of
  // `prepLine`; the press is the step that blocks the deploy
  // (`prepStep`), and ⋯ holds the rest. The rows stand under two heads,
  // "In the pens · combat" and "· support", with the weight in use and
  // the cap of BUILD · SHIPYARD (R1570); a head with no hull under it
  // is left out. The captain choice and the LOADOUT sheet are picker cards
  // (R1893, R2031): the context slot of a desktop, a page of the menu
  // sheet on a telephone.
  const docked = s.penned();
  if (docked.length === 0) {
    html += `<h3>${S.pens_section_head}</h3>`
      + emptyGoHtml(S.pens_section_empty, 'BUILD', 'SHIPYARD');
  }
  const dialog = ctx.dialog;
  for (const support of [false, true]) {
    const rows = docked.filter((st) => supportCls(st.cls) === support);
    if (rows.length === 0 || !e) continue;
    html += `<h3>${support
      ? S.pens_section_head_support({ w: e.supportWeight, cap: e.supportCap })
      : S.pens_section_head_combat({ w: e.combatWeight, cap: e.combatCap })}</h3>`;
    for (const st of rows) {
      html += fleetRowHtml(dockedRow(ctx.card, st, {
        look, moreOpen: ctx.rowMore === st.fleetId, picking: ctx.deploying === st.fleetId,
        dialog: dialog && dialog.fleetId === st.fleetId ? dialog.kind : '',
        breakArmed: ctx.armed(`breakfleet:${st.fleetId}`),
        selected: sel?.kind === 'fleet' && sel.id === st.fleetId,
      }), env);
    }
  }
  return html;
}
